<template>
    <div>
        <section class="w-full flex flex-col">
            <header
                class="flex justify-between items-start sm:items-center mb-5 flex-col sm:flex-row gap-1 sm:gap-0"
            >
                <h1 class="text-2xl font-semibold text-primary">My Benefits</h1>
                <a
                    href="#"
                    @click.prevent="showBenefitsDisclosureModal = true"
                    class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-sm rounded px-1"
                    id="hs-mybenefits-benefits-disclosure"
                    role="button"
                >
                    Benefits Disclosure
                </a>
            </header>
        </section>

        <FreemiumBanner />

        <BenefitsTabs v-model="activeTab" />

        <LoadingState v-if="loading && activeTab === 'current'" :loading="loading" />

        <!-- Error State -->
        <div
            v-else-if="hasError && activeTab === 'current'"
            class="min-h-[calc(100vh-200px)] flex items-center justify-center"
        >
            <EmptyState
                icon="warning"
                title="Unable to load benefits"
                description="We're having trouble loading your card benefits. Please check your connection and try again."
                :hasAction="true"
                actionLabel="Try again"
                @action="retryFetch"
            />
        </div>

        <InsightsView v-else-if="activeTab === 'insights'" />

        <template v-else>
            <BenefitsStats v-if="hasBenefits || hasActiveFilters" />

            <!-- Search input -->
            <div
                v-if="hasBenefits || hasActiveFilters || trackedCardHasNoBenefits"
                class="flex w-full p-2 flex-col items-start gap-2.5 rounded-lg bg-white dark:bg-slate-800 shadow-sm mb-0 sm:mb-4"
            >
                <div class="relative w-full">
                    <div
                        class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
                    >
                        <Icon icon="search" class="h-5 w-5 text-slate-800 dark:text-slate-100" />
                    </div>
                    <input
                        v-model="search"
                        type="text"
                        class="block w-full pl-12 pr-12 py-2.5 border-0 rounded-lg text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 bg-transparent placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:placeholder-slate-400 focus:ring-1 focus:ring-slate-800 dark:focus:ring-slate-300 focus:ring-inset"
                        placeholder="Search benefits"
                        aria-label="Search benefits"
                    />
                    <button
                        v-if="search && search.trim() !== ''"
                        type="button"
                        class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100"
                        @click="clearSearch"
                        aria-label="Clear search"
                    >
                        <Icon icon="close" class="w-3 h-3" />
                    </button>
                </div>
            </div>

            <!-- Conditional Filters: Desktop vs Mobile -->
            <BenefitsFilters
                v-if="
                    hasBenefits || hasActiveFilters || trackedCardHasNoBenefits
                "
            />

            <!-- Tracked Cards Selector (Mobile only, for free users) -->
            <TrackedCardsSelector
                class="sm:hidden"
                @open-modal="openSelectCardsModal"
                @open-upgrade="cardBenefitsStore.setShowUpgradeModal(true)"
            />

            <!-- Conditional Table: Desktop vs Mobile -->
            <component
                :is="isMobile ? BenefitsTableMobile : BenefitsTable"
                v-if="hasBenefits"
                :filtered-benefits="filteredBenefits"
                :view-mode="viewMode"
                :has-active-filters="hasActiveFilters"
                @usage-added="handleUsageAdded"
                @view-details="handleViewDetails"
                @clear-filters="clearFilters"
            />

            <div
                v-if="
                    !hasBenefits &&
                    !hasActiveFilters &&
                    trackedCardHasNoBenefits
                "
                class="min-h-[300px] flex items-center justify-center"
            >
                <EmptyState
                    icon="coin"
                    title="No trackable benefits"
                    :description="`${trackedCardName || 'This card'} has no trackable benefits. Select a different card to track.`"
                    :hasAction="false"
                />
            </div>
            <div
                v-if="
                    !hasBenefits &&
                    !hasActiveFilters &&
                    !trackedCardHasNoBenefits
                "
                class="min-h-[calc(100vh-200px)] flex items-center justify-center"
            >
                <EmptyState
                    icon="coin"
                    title="No benefits yet"
                    description="No trackable benefits found for cards in your wallet."
                    :hasAction="true"
                    actionLabel="View my wallet"
                    @action="goToWallet"
                />
            </div>
            <div
                v-if="!hasBenefits && hasActiveFilters"
                class="min-h-[calc(100vh-200px)] flex items-center justify-center"
            >
                <EmptyState
                    title="No matching benefits"
                    description="Try removing some filters to see more benefits."
                    :hasAction="true"
                    actionLabel="Reset filters"
                    @action="clearFilters"
                />
            </div>
        </template>

        <!-- Select Cards Modal -->
        <SelectCardsModal v-model="showSelectCardsModal" />

        <!-- MY1-3080: "Add from My Spending" transaction picker, opened from
             any surface's Add usage menu -->
        <TransactionPickerModal
            :is-open="cardBenefitsStore.transactionPicker.open"
            :benefit="cardBenefitsStore.transactionPicker.benefit"
            :remaining-amount="cardBenefitsStore.transactionPicker.remainingAmount"
            @close="cardBenefitsStore.closeTransactionPicker()"
        />

        <!-- Upgrade Modal -->
        <UpgradeModal
            :show="showUpgradeModal"
            @close="cardBenefitsStore.setShowUpgradeModal(false)"
        />

        <!-- Benefits Disclosure Modal -->
        <BenefitsDisclosureModal
            :is-open="showBenefitsDisclosureModal"
            @close="showBenefitsDisclosureModal = false"
        />
    </div>
</template>
<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { useIsMobile } from '@/composables/useIsMobile';
import BenefitsFilters from '@/components/card-benefits/benefits-filters.vue';
import BenefitsStats from '@/components/card-benefits/benefits-stats.vue';
import BenefitsTable from '@/components/card-benefits/benefits-table.vue';
import BenefitsTableMobile from '@/components/card-benefits/benefits-table-mobile.vue';
import BenefitsTabs from '@/components/card-benefits/benefits-tabs.vue';
import InsightsView from '@/components/card-benefits/insights/insights-view.vue';
import FreemiumBanner from '@/components/card-benefits/freemium-banner.vue';
import TrackedCardsSelector from '@/components/card-benefits/tracked-cards-selector.vue';
import SelectCardsModal from '@/components/card-benefits/select-cards-modal.vue';
import TransactionPickerModal from '@/components/card-benefits/transaction-picker-modal.vue';
import UpgradeModal from '@/modals/upgrade-modal.vue';
import BenefitsDisclosureModal from '@/modals/benefits-disclosure-modal.vue';

import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { storeToRefs } from 'pinia';

const cardBenefitsStore = useCardBenefitsStore();
const {
    filteredBenefits,
    loading,
    hasActiveFilters,
    viewMode,
    showAddUsageModal,
    hasError,
    error,
    trackedCardHasNoBenefits,
    trackedCardName,
    isSubscribed,
    showSelectCardsModal,
    showUpgradeModal,
    insightsActiveTab: activeTab,
} = storeToRefs(cardBenefitsStore);
const { isMobile } = useIsMobile();

const search = ref('');
const showBenefitsDisclosureModal = ref(false);

const clearFilters = () => {
    search.value = '';
    cardBenefitsStore.clearFilters();
};
let searchDebounceHandle = null;

const clearSearch = () => {
    search.value = '';
    cardBenefitsStore.setSearchQuery('');
};

watch(search, (newVal) => {
    if (searchDebounceHandle) clearTimeout(searchDebounceHandle);
    const term = (newVal || '').toString();
    if (!term.trim()) {
        cardBenefitsStore.setSearchQuery('');
        return;
    }
    searchDebounceHandle = setTimeout(() => {
        cardBenefitsStore.setSearchQuery(term);
    }, 450);
});

// Sync local input back when the store is cleared externally (e.g. child filter components)
watch(() => cardBenefitsStore.searchQuery, (storeVal) => {
    if (storeVal === '' && search.value !== '') {
        search.value = '';
        if (searchDebounceHandle) clearTimeout(searchDebounceHandle);
    }
});

// Selected data for modals
const selectedBenefit = ref({});

const hasBenefits = computed(() => {
    return filteredBenefits.value && filteredBenefits.value.length > 0;
});

const goToWallet = () => {
    router.visit('/wallet');
};

const openSelectCardsModal = () => {
    cardBenefitsStore.setShowSelectCardsModal(true);
};

const handleViewDetails = (benefit) => {
    selectedBenefit.value = benefit;
    if (isMobile.value) {
        // Mobile view details handled by mobile component
    } else {
        // Handle desktop sidebar logic
        cardBenefitsStore.openBenefitDetails(benefit);
    }
};

const handleUsageAdded = async () => {
    // await cardBenefitsStore.fetchBenefits();
    if (showAddUsageModal.value) {
        cardBenefitsStore.setShowAddUsageModal(false);
    }
};

const retryFetch = async () => {
    await cardBenefitsStore.fetchBenefits();
};

const page = usePage();
const walletId = page.props.userData.wallet_id;

onMounted(async () => {
    cardBenefitsStore.setWalletId(walletId);
    await cardBenefitsStore.fetchBenefits();

    // Deep link support: open benefit sidebar if query params are present
    const params = new URLSearchParams(window.location.search);
    const benefitId = params.get('benefit_id');
    const accountId = params.get('account_id');

    if (benefitId && accountId) {
        cardBenefitsStore.openBenefitByIds(
            Number(benefitId),
            Number(accountId)
        );
    }
});
</script>
